import React from 'react';
import Icon from '../../../components/AppIcon';

const ProblemSolutionSection = () => {
  const problems = [
    {
      icon: 'AlertCircle',
      title: 'Overwhelming First Days',
      description: 'New hires face information overload with scattered resources across multiple platforms, leading to confusion and anxiety'
    },
    {
      icon: 'Clock',
      title: 'Manager Time Drain',
      description: 'Team leads spend hours answering repetitive questions instead of focusing on strategic initiatives'
    },
    {
      icon: 'TrendingDown',
      title: 'Slow Productivity Ramp',
      description: 'Traditional onboarding takes weeks before employees become productive contributors'
    }
  ];

  const solutions = [
    {
      icon: 'Target',
      title: 'Structured Onboarding Journey',
      description: 'Gamified checklist system guides new hires through essential tasks with clear progression and dopamine-hit celebrations'
    },
    {
      icon: 'Bot',
      title: 'AI-Powered Assistant "Bee"',
      description: 'Instant answers to common questions with company knowledge base integration, reducing manager interruptions by 80%'
    },
    {
      icon: 'Rocket',
      title: 'Accelerated Time-to-Productivity',
      description: 'Systematic knowledge delivery and interactive learning modules help employees contribute value in days, not weeks'
    }
  ];

  return (
    <section className="py-20 px-6 bg-secondary/20">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            The Onboarding Challenge
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            High-growth tech companies struggle with chaotic employee onboarding that impacts retention, productivity, and team morale
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 mb-20">
          <div>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-error/20 rounded-lg flex items-center justify-center">
                <Icon name="X" size={24} color="var(--color-error)" strokeWidth={2.5} />
              </div>
              <h3 className="text-2xl font-bold text-foreground">The Problem</h3>
            </div>
            <div className="space-y-6">
              {problems?.map((problem, index) => (
                <div key={index} className="bg-card border border-border rounded-lg p-6 hover:shadow-elevation-1 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center shrink-0">
                      <Icon name={problem?.icon} size={20} color="var(--color-error)" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-foreground mb-2">{problem?.title}</h4>
                      <p className="text-muted-foreground">{problem?.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-success/20 rounded-lg flex items-center justify-center">
                <Icon name="CheckCircle2" size={24} color="var(--color-success)" strokeWidth={2.5} />
              </div>
              <h3 className="text-2xl font-bold text-foreground">Our Solution</h3>
            </div>
            <div className="space-y-6">
              {solutions?.map((solution, index) => (
                <div key={index} className="bg-card border border-border rounded-lg p-6 hover:shadow-elevation-1 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center shrink-0">
                      <Icon name={solution?.icon} size={20} color="var(--color-success)" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-foreground mb-2">{solution?.title}</h4>
                      <p className="text-muted-foreground">{solution?.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 border border-primary/20 rounded-lg p-8 text-center">
          <h3 className="text-2xl font-bold text-foreground mb-3">
            Transform Your Onboarding Experience Today
          </h3>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Join forward-thinking companies that have revolutionized their employee onboarding process with Bee Wise
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex items-center gap-2 text-success">
              <Icon name="Shield" size={20} color="var(--color-success)" />
              <span className="text-sm font-medium">Enterprise Security</span>
            </div>
            <div className="flex items-center gap-2 text-success">
              <Icon name="Lock" size={20} color="var(--color-success)" />
              <span className="text-sm font-medium">SOC 2 Compliant</span>
            </div>
            <div className="flex items-center gap-2 text-success">
              <Icon name="Award" size={20} color="var(--color-success)" />
              <span className="text-sm font-medium">GDPR Ready</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSolutionSection;